import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { MUSIC24_COVER_AI_PICTURES } from 'fym-shared/src/music24/coverEditorPlans';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { SMLUpgradeFeature } from '../../../music24/config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../../music24/hooks/useUpgradePrompt';
import { coverEditorMessages } from '../../coverEditorMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AiLockedProps {
  /** Opens the bank. Absent: no bank link (the bank is already on screen). */
  onOpenBank?: () => void;
  /** True for the card that the editor pins on the Free plan: it does not shrink in the panel. */
  isPinned?: boolean;
}

/**
 * The card of a plan with no AI (Free and the trial, owner decision D6; design cover-editor c6):
 * what a paid plan gives, the way to the plans, and the way to the bank, which is free. On the
 * Free plan the editor pins it at the top of every panel (owner request 2026-10-09: "this alert
 * should always be seen"); the trial sees it in the AI tab after its welcome run.
 */
const AiLockedComponent: React.FC<AiLockedProps> = ({ onOpenBank, isPinned = false }) => {
  const intl = useIntl();
  const openUpgrade = useUpgradePrompt();
  const upgrade = useCallback(() => openUpgrade(SMLUpgradeFeature.generic), [openUpgrade]);
  // The smallest month count of a plan with AI, with no plan name in the code.
  const from = useMemo(() => Math.min(...Object.values(MUSIC24_COVER_AI_PICTURES).filter((count) => count > 0)), []);
  return (
    <div className={clsx(styles.lock, isPinned && styles.lockPinned)} data-testid="cover-ai-upsell">
      <b>{intl.formatMessage(coverEditorMessages.aiLockedTitle)}</b>
      <p>{intl.formatMessage(coverEditorMessages.aiLockedText, { count: from })}</p>
      <SMLButton size="sm" omitCap onClick={upgrade}>
        {intl.formatMessage(coverEditorMessages.aiLockedUpgrade)}
      </SMLButton>
      {onOpenBank !== undefined && (
        <button type="button" className={styles.linkButton} onClick={onOpenBank}>
          {intl.formatMessage(coverEditorMessages.aiLockedBank)}
        </button>
      )}
    </div>
  );
};

export const AiLocked = TypedMemo(AiLockedComponent);
